import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { ACTIVITY_MAP_VIEW_SPEC, toggleOverride } from './dev-feature-toggles';

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

const cached: Partial<Record<string, Promise<boolean>>> = {};

function gate(spec: string, route: string): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(spec);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  const hit = cached[spec];
  if (hit) return hit;
  const promise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/${route}`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  cached[spec] = promise;
  return promise;
}

export function activityMapViewEnabled(): Promise<boolean> {
  return gate(ACTIVITY_MAP_VIEW_SPEC, 'activity-map-view');
}

/**
 * The view-switch answer the home resolved with, fixed for the page session. A conduct that missed the deadline is
 * recorded as `false`, and its late answer is dropped: the home has already mounted as the task list, and a switch
 * appearing under the reader a moment later is the flash this gate exists to prevent.
 */
let viewSwitchDecision: boolean | undefined;

/**
 * Whether the view-switch answer is in, so the home can hold its loader until then instead of mounting as the task
 * list and growing a switch a moment later. Gives up at `GATE_TIMEOUT_MS` and records the miss as control.
 */
export function useActivityMapViewSwitchDecided(): boolean {
  const [decided, setDecided] = useState(
    () => toggleOverride(ACTIVITY_MAP_VIEW_SPEC) !== undefined || viewSwitchDecision !== undefined,
  );
  useEffect(() => {
    if (decided) return undefined;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const missed = new Promise<false>((resolve) => {
      timer = setTimeout(() => resolve(false), GATE_TIMEOUT_MS);
    });
    void Promise.race([activityMapViewEnabled(), missed]).then((value) => {
      clearTimeout(timer);
      if (viewSwitchDecision === undefined) viewSwitchDecision = value;
      if (live) setDecided(true);
    });
    return () => {
      live = false;
    };
  }, [decided]);
  return decided;
}

/**
 * Whether the activity-map home shows its task-list ⇄ map switch — and so whether the map view (the octopus) can be
 * reached at all: the Conductor release `symphonyHomeActivityMapView`, ONE id for the desktop, mobile web and both
 * phones (their `activityMapViewSwitchEnabled`). Off, unresolved or failed → no switch, the task list only.
 *
 * `useActivityMapViewSwitchDecided` has already decided by the time this mounts on a normal load; a reader who
 * reaches the home undecided (a late mount) is asked then. A Dev Tools override wins either way.
 */
export function useActivityMapViewSwitch(): boolean {
  const [shown, setShown] = useState(() => {
    const forced = toggleOverride(ACTIVITY_MAP_VIEW_SPEC);
    return forced !== undefined ? forced === 'true' : viewSwitchDecision === true;
  });
  useEffect(() => {
    if (toggleOverride(ACTIVITY_MAP_VIEW_SPEC) !== undefined || viewSwitchDecision !== undefined) return undefined;
    let live = true;
    void activityMapViewEnabled().then((value) => {
      viewSwitchDecision = value;
      if (live) setShown(value);
    });
    return () => {
      live = false;
    };
  }, []);
  const forced = toggleOverride(ACTIVITY_MAP_VIEW_SPEC);
  return forced !== undefined ? forced === 'true' : shown;
}
